/**
 * Wallet QBit SDK - QBit 钱包组件
 */

import React, { useState } from 'react';
import { useWalletQBit, useInvestProducts } from '../hooks/useWalletQBit';

/**
 * 资产组合概览
 */
export function PortfolioOverview() {
  const { portfolio, balances, loading, totalValue } = useWalletQBit();

  if (loading) {
    return <div className="eco-portfolio-loading">加载中...</div>;
  }

  return (
    <div className="eco-portfolio-overview">
      <div className="eco-portfolio-header">
        <h3>资产总览</h3>
        <div className="eco-portfolio-value">
          <span className="eco-value-label">总资产</span>
          <span className="eco-value-amount">
            ${totalValue.toLocaleString()}
          </span>
        </div>
      </div>

      <div className="eco-portfolio-stats">
        <div className="eco-stat-item">
          <span className="eco-stat-label">今日盈亏</span>
          <span className={`eco-stat-value ${portfolio?.today_pnl >= 0 ? 'positive' : 'negative'}`}>
            {portfolio?.today_pnl >= 0 ? '+' : ''}{portfolio?.today_pnl?.toFixed(2) || '0.00'}%
          </span>
        </div>
        <div className="eco-stat-item">
          <span className="eco-stat-label">累计收益</span>
          <span className={`eco-stat-value ${portfolio?.total_pnl >= 0 ? 'positive' : 'negative'}`}>
            ${portfolio?.total_pnl?.toLocaleString() || '0'}
          </span>
        </div>
      </div>

      <div className="eco-asset-list">
        {balances.map((asset) => (
          <div key={asset.asset_id} className="eco-asset-item">
            <div className="eco-asset-icon">{asset.icon || '💰'}</div>
            <div className="eco-asset-info">
              <span className="eco-asset-name">{asset.name}</span>
              <span className="eco-asset-balance">{asset.balance} {asset.symbol}</span>
            </div>
            <div className="eco-asset-value">
              ${asset.value?.toLocaleString() || '0'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * 投资产品列表
 */
export function InvestProductList() {
  const { products, loading, subscribe } = useInvestProducts();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [amount, setAmount] = useState('');

  const handleSubscribe = async () => {
    if (!selectedProduct || !amount) return;
    try {
      await subscribe(selectedProduct.id, amount);
      setSelectedProduct(null);
      setAmount('');
    } catch (err) {
      // 错误处理
    }
  };

  if (loading) {
    return <div className="eco-products-loading">加载中...</div>;
  }

  return (
    <div className="eco-invest-products">
      <h3>理财产品</h3>
      
      <div className="eco-products-grid">
        {products.map((product) => (
          <div key={product.id} className="eco-product-card">
            <div className="eco-product-header">
              <span className="eco-product-name">{product.name}</span>
              <span className="eco-product-tag">{product.type}</span>
            </div>
            <div className="eco-product-apy">
              <span className="eco-apy-value">{product.apy}%</span>
              <span className="eco-apy-label">年化收益</span>
            </div>
            <div className="eco-product-info">
              <span>期限: {product.term || '活期'}</span>
              <span>起投: ${product.min_amount}</span>
            </div>
            <button 
              className="eco-btn eco-btn-primary eco-btn-sm"
              onClick={() => setSelectedProduct(product)}
            >
              立即申购
            </button>
          </div>
        ))}
      </div>

      {/* 申购模态框 */}
      {selectedProduct && (
        <div className="eco-modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="eco-modal" onClick={(e) => e.stopPropagation()}>
            <div className="eco-modal-header">
              <h3>申购 {selectedProduct.name}</h3>
              <button className="eco-modal-close" onClick={() => setSelectedProduct(null)}>
                ×
              </button>
            </div>
            <div className="eco-modal-body">
              <div className="eco-form-group">
                <label>申购金额</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`最低 ${selectedProduct.min_amount}`}
                  min={selectedProduct.min_amount}
                />
              </div>
              <div className="eco-subscribe-info">
                <p>年化收益: {selectedProduct.apy}%</p>
                <p>预计收益: ${(parseFloat(amount || 0) * selectedProduct.apy / 100).toFixed(2)}/年</p>
              </div>
            </div>
            <div className="eco-modal-footer">
              <button 
                className="eco-btn" 
                onClick={() => setSelectedProduct(null)}
              >
                取消
              </button>
              <button 
                className="eco-btn eco-btn-primary"
                onClick={handleSubscribe}
                disabled={!amount || parseFloat(amount) < selectedProduct.min_amount}
              >
                确认申购
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 我的投资
 */
export function MyInvestments() {
  const { myInvestments, loading, redeem } = useInvestProducts();

  if (loading) {
    return <div className="eco-investments-loading">加载中...</div>;
  }

  if (myInvestments.length === 0) {
    return (
      <div className="eco-investments-empty">
        <p>暂无投资</p>
        <span>开始投资，获取稳定收益</span>
      </div>
    );
  }

  return (
    <div className="eco-my-investments">
      <h3>我的投资</h3>
      
      <div className="eco-investments-list">
        {myInvestments.map((investment) => (
          <div key={investment.id} className="eco-investment-item">
            <div className="eco-investment-info">
              <span className="eco-investment-name">{investment.product_name}</span>
              <span className="eco-investment-amount">
                ${investment.amount.toLocaleString()}
              </span>
            </div>
            <div className="eco-investment-stats">
              <span className="eco-earning positive">
                +${investment.earning.toFixed(2)}
              </span>
              <span className="eco-apy">{investment.apy}%</span>
            </div>
            <button 
              className="eco-btn eco-btn-sm eco-btn-outline"
              onClick={() => redeem(investment.product_id, investment.amount)}
            >
              赎回
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

